import {
  all,
  call,
  delay,
  put,
  select,
  takeLatest,
} from 'typed-redux-saga/macro';

import BackendService from 'src/services/BackendService';
import { DEFAULT_PAGINATION } from 'src/utils/constants';
import { notifyUserByActionTypeAndCode } from 'src/utils/errorHandling/notifications';
import { tenantErrorCodes } from 'src/utils/errorHandling/errorCodes';
import { MODAL_STATUS } from 'src/components/Modal/constants';
import { actions } from './slice';
import { DATA_FETCHING_STATUS } from '../../constants';
import { getUserDevicesList } from '../../device/modules/saga';
import { selectors as deviceSelectors } from '../../device/modules/slice';
import { hasAllPermissions } from 'src/utils/permissions';
import permissions from 'src/permissions';
import { getAddOrEditPayload } from './utils';

function* getSiteGroups(action: ReturnType<typeof actions.getSiteGroups>) {
  try {
    if (hasAllPermissions(permissions.VIEW_DEVICES_TENANT_COLUMN)) {
      yield* put(actions.setStatus(DATA_FETCHING_STATUS.LOADING));
      yield* delay(500);
      const siteGroupsResponse = yield* call(
        BackendService.getSiteGroups,
        action.payload,
      );
      yield* put(
        actions.setSiteGroups({
          data: siteGroupsResponse.data.data,
          pageMetadata: siteGroupsResponse.data.metadata.page,
        }),
      );
      yield* put(actions.setStatus(DATA_FETCHING_STATUS.SUCCESS));
    }
  } catch (e) {
    console.error('error in getSiteGroups: ', e);
    yield* put(actions.setStatus(DATA_FETCHING_STATUS.ERROR));
  }
}

function* getCurrentUserGroups() {
  try {
    yield* put(
      actions.setCurrentUserGroupsStatus(DATA_FETCHING_STATUS.LOADING),
    );
    const customerSiteGroupsResponse = yield* call(
      BackendService.getCurrentUserGroups,
    );

    yield* put(actions.setCurrentUserGroups(customerSiteGroupsResponse.data));
    yield* put(
      actions.setCurrentUserGroupsStatus(DATA_FETCHING_STATUS.SUCCESS),
    );
  } catch (e) {
    console.error('error in getSiteGroups: ', e);
    yield* put(actions.setCurrentUserGroupsStatus(DATA_FETCHING_STATUS.ERROR));
  }
}

function* getCurrentSiteGroups(
  action: ReturnType<typeof actions.getCurrentSiteGroups>,
) {
  try {
    const { id } = action.payload;
    if (!id) {
      return;
    }
    yield* put(
      actions.setCurrentSiteGroupsStatus(DATA_FETCHING_STATUS.LOADING),
    );
    const currentSiteGroupsResponse = yield* call(
      BackendService.getCurrentSiteGroups,
      id,
    );
    yield* put(
      actions.setCurrentSiteGroups({
        data: currentSiteGroupsResponse.data,
      }),
    );
    yield* put(
      actions.setCurrentSiteGroupsStatus(DATA_FETCHING_STATUS.SUCCESS),
    );
  } catch (e) {
    console.error('error in getSiteGroups: ', e);
    yield* put(actions.setCurrentSiteGroupsStatus(DATA_FETCHING_STATUS.ERROR));
  }
}
function* getSiteGroupsTableData(
  action: ReturnType<typeof actions.getSiteGroupsTableData>,
) {
  try {
    if (hasAllPermissions(permissions.VIEW_DEVICES_TENANT_COLUMN)) {
      yield* put(
        actions.setSiteGroupsTableDataStatus(DATA_FETCHING_STATUS.LOADING),
      );
      yield* delay(500);
      const siteGroupsTableResponse = yield* call(
        BackendService.getSiteGroupsTableData,
        action.payload,
      );
      yield* put(
        actions.setSiteGroupsTableData({
          data: siteGroupsTableResponse.data.data,
          pageMetadata: siteGroupsTableResponse.data.metadata.page,
        }),
      );
      yield* put(
        actions.setSiteGroupsTableDataStatus(DATA_FETCHING_STATUS.SUCCESS),
      );
    }
  } catch (e) {
    console.error('error in getSiteGroupsTableData: ', e);
    yield* put(
      actions.setSiteGroupsTableDataStatus(DATA_FETCHING_STATUS.ERROR),
    );
  }
}

function* deleteSiteGroup(action: ReturnType<typeof actions.deleteSiteGroup>) {
  const { groupId, customerID, page, limit, searchKeyword } = action.payload;

  const getDevicesParamsAction = {
    payload: {
      page: DEFAULT_PAGINATION.PAGE - 1,
      limit: DEFAULT_PAGINATION.LIMIT_50,
      filters: {
        tenantId: groupId,
      },
    },
    type: '',
  };

  try {
    yield* call(getUserDevicesList, getDevicesParamsAction);
    const deviceListStatus = yield* select(deviceSelectors.getStatus);
    const devicesList = yield* select(deviceSelectors.getDevicesList);
    const amountOfAttachedDevicesToCurrentTenant = devicesList?.filter(
      device => device.tenantId === groupId,
    ).length;

    if (deviceListStatus !== DATA_FETCHING_STATUS.SUCCESS) {
      notifyUserByActionTypeAndCode(
        action.type,
        groupId,
        tenantErrorCodes.COULD_NOT_GET_DEVICE_DATA,
      );
    } else if (amountOfAttachedDevicesToCurrentTenant === 0) {
      yield* call(BackendService.deleteSiteGroup, groupId);
      yield* put(
        actions.getSiteGroupsTableData({
          page,
          limit,
          customerID,
          search: searchKeyword,
          cache: customerID ? 'false' : 'true',
        }),
      );
    } else {
      notifyUserByActionTypeAndCode(
        action.type,
        groupId,
        tenantErrorCodes.DEVICES_ATTACHED_TO_TENANT_ERROR,
      );
    }
  } catch (error) {
    console.error('error in deleteSiteGroup: ', error);
    notifyUserByActionTypeAndCode(action.type, groupId, error);
  }
}

function* createSiteGroup(action: ReturnType<typeof actions.createSiteGroup>) {
  const formData = action.payload;
  const createSiteGroupRequest = getAddOrEditPayload(formData);

  try {
    yield* put(actions.setModalStatus(MODAL_STATUS.SUBMITTING));
    yield* call(BackendService.createSiteGroups, createSiteGroupRequest);
    yield* put(actions.setModalStatus(MODAL_STATUS.SUCCESS));
  } catch (error) {
    console.error('error in createSiteGroup: ', error);
    yield* put(actions.setModalStatus(MODAL_STATUS.ERROR));
    notifyUserByActionTypeAndCode(
      action.type,
      createSiteGroupRequest?.name,
      error,
    );
  }
}

function* editSiteGroup(action: ReturnType<typeof actions.editSiteGroup>) {
  const { id, data: formData } = action.payload;
  const editSiteGroupRequest = getAddOrEditPayload(formData);
  try {
    yield* put(actions.setModalStatus(MODAL_STATUS.SUBMITTING));
    yield* call(BackendService.updateSiteGroup, id, editSiteGroupRequest);
    yield* put(actions.setModalStatus(MODAL_STATUS.SUCCESS));
  } catch (error) {
    console.error('error in editSiteGroup: ', error);
    yield* put(actions.setModalStatus(MODAL_STATUS.ERROR));
    notifyUserByActionTypeAndCode(
      action.type,
      editSiteGroupRequest?.name,
      error,
    );
  }
}
function* fetchSiteGroupDisconnectionOverview(
  action: ReturnType<typeof actions.fetchSiteGroupDisconnectionOverview>,
) {
  try {
    const { groupId, continuousDisconnectionSeconds, currentPage, pageSize } =
      action.payload;
    yield* put(
      actions.fetchSiteGroupDisconnectionOverviewStatus(
        DATA_FETCHING_STATUS.LOADING,
      ),
    );
    const { data } = yield* call(
      BackendService.fetchSiteGroupDisconnectionOverview,
      continuousDisconnectionSeconds,
      groupId,
      currentPage,
      pageSize,
    );
    const { data: groupLocations } = yield* call(
      BackendService.fetchSiteGroupLocations,
      groupId,
    );
    const { groupId: groupIdResponse, disconnectedDevices, metadata } = data;
    const disconnectedDevicesWithLocations = disconnectedDevices.map(device => {
      const roomData = groupLocations?.rooms.find(room =>
        room.beds.find(bed => bed.deviceId === device?.manufacturerId),
      );
      const roomName = roomData?.name ?? '';
      const bedName = roomData?.beds[0]?.name ?? '';

      return {
        ...device,
        roomName,
        bedName,
        deviceLocation: `${roomName}${bedName}`,
      };
    });
    yield* put(
      actions.fetchSiteGroupDisconnectionOverviewSuccess({
        groupId: groupIdResponse,
        disconnectedDevices: disconnectedDevicesWithLocations,
        metaData: metadata?.page ?? {
          totalResults: 0,
          page: 0,
          limit: 0,
        },
      }),
    );
    yield* put(
      actions.fetchSiteGroupDisconnectionOverviewStatus(
        DATA_FETCHING_STATUS.SUCCESS,
      ),
    );
  } catch (e) {
    console.error(`error in fetchSiteGroupDisconnectionOverview`, e);
    yield* put(
      actions.fetchSiteGroupDisconnectionOverviewStatus(
        DATA_FETCHING_STATUS.ERROR,
      ),
    );
  }
}

export default function* watchSiteGroupsActions() {
  yield* all([
    takeLatest(actions.deleteSiteGroup, deleteSiteGroup),
    takeLatest(actions.createSiteGroup, createSiteGroup),
    takeLatest(actions.editSiteGroup, editSiteGroup),
    takeLatest(actions.getSiteGroups, getSiteGroups),
    takeLatest(actions.getCurrentSiteGroups, getCurrentSiteGroups),
    takeLatest(actions.getSiteGroupsTableData, getSiteGroupsTableData),
    takeLatest(actions.getCurrentUserGroups, getCurrentUserGroups),
    takeLatest(
      actions.fetchSiteGroupDisconnectionOverview,
      fetchSiteGroupDisconnectionOverview,
    ),
  ]);
}
